import alertaService from '../../services/Alerta/AlertaService';

/**
 * Registro de lectura de alertas (auditoria) y fecha local.
 *
 * Dos reglas del negocio (2026-09-29):
 *  - Cada apertura queda registrada: cuando, desde donde, IP (la pone el
 *    servidor) y GPS.
 *  - Si el GPS no se puede obtener -- permiso negado, sin señal, demora --
 *    la apertura se graba igual, con el motivo. Nunca se espera mas de
 *    ESPERA_GPS_MS por la ubicacion.
 */

export const ESPERA_GPS_MS = 3000;

/**
 * aaaa-mm-dd de la fecha LOCAL del equipo, menos n dias.
 *
 * No usar toISOString(): devuelve la fecha UTC, y en Ecuador (UTC-5) desde las
 * 19:00 ya es "mañana". Por eso la consulta arrancaba en el 30 siendo 29.
 */
export const fechaLocalIso = (n = 0, ahora = new Date()) => {
    const d = new Date(ahora.getTime());
    d.setDate(d.getDate() - n);
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${d.getFullYear()}-${mm}-${dd}`;
};

/** WEB_MOVIL si se abre desde un celular o tablet, WEB si no. */
export const origenActual = () => {
    if (typeof window === 'undefined') return 'WEB';
    const angosto = window.matchMedia ? window.matchMedia('(max-width: 768px)').matches : false;
    const tactil = typeof navigator !== 'undefined'
        && /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent || '');
    return angosto || tactil ? 'WEB_MOVIL' : 'WEB';
};

/**
 * La ubicacion del equipo, sin fallar nunca: siempre resuelve, a lo sumo en
 * ESPERA_GPS_MS. Si no hay coordenadas devuelve el motivo.
 */
export const ubicacionRapida = (geo = (typeof navigator !== 'undefined' ? navigator.geolocation : null),
    esperaMs = ESPERA_GPS_MS) => new Promise((resolve) => {
    if (!geo || typeof geo.getCurrentPosition !== 'function') {
        resolve({ ubicacionEstado: 'NO_SOPORTADA' });
        return;
    }
    let listo = false;
    const fin = (v) => { if (!listo) { listo = true; resolve(v); } };
    // Tope propio: algunos navegadores no respetan el timeout de la API.
    const reloj = setTimeout(() => fin({ ubicacionEstado: 'TIEMPO_AGOTADO' }), esperaMs + 200);
    try {
        geo.getCurrentPosition(
            (p) => {
                clearTimeout(reloj);
                fin({
                    latitud: p.coords.latitude,
                    longitud: p.coords.longitude,
                    precision: p.coords.accuracy,
                    ubicacionEstado: 'OK'
                });
            },
            (err) => {
                clearTimeout(reloj);
                const motivo = err && err.code === 1 ? 'DENEGADA'
                    : err && err.code === 3 ? 'TIEMPO_AGOTADO' : 'NO_DISPONIBLE';
                fin({ ubicacionEstado: motivo });
            },
            // maximumAge: una posicion de hasta 2 minutos alcanza y evita esperar al GPS.
            { enableHighAccuracy: false, timeout: esperaMs, maximumAge: 120000 }
        );
    } catch (e) {
        clearTimeout(reloj);
        fin({ ubicacionEstado: 'NO_DISPONIBLE' });
    }
});

/**
 * Registra que se abrieron estas alertas. Nunca rechaza: si el endpoint nuevo
 * falla, cae a marcar leida con el endpoint de siempre (si hay id de aviso),
 * para que la lectura no se pierda.
 *
 * @param {number[]} monitoreoDetIds
 * @param {string}   pantalla  MIS_ALERTAS | CONSULTA | VIAJE | CAMPANITA
 * @param {object}   extra     { lote, avisoId }
 * @returns {Promise<object|null>} porAlerta del backend, o null si no se pudo
 */
export const registrarLectura = async (monitoreoDetIds, pantalla, extra = {}) => {
    const ids = (monitoreoDetIds || []).filter((x) => x != null);
    if (!ids.length) return null;
    const ubicacion = await ubicacionRapida();
    try {
        const r = await alertaService.registrarVista({
            monitoreoDetIds: ids,
            pantalla,
            origen: origenActual(),
            lote: extra.lote || null,
            ...ubicacion
        });
        return r?.ok ? (r.porAlerta || {}) : null;
    } catch (e) {
        if (extra.avisoId) {
            try { await alertaService.marcarLeida(extra.avisoId); } catch (e2) { /* se reintenta al volver a abrir */ }
        }
        return null;
    }
};

/** Un id para agrupar las filas de una misma revision de viaje. */
export const nuevoLote = () => {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
    return `L${Date.now()}${Math.random().toString(16).slice(2, 10)}`;
};
